import { useReducer, useState } from 'react'
import { Pressable, Text, TextInput, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useRouter } from 'expo-router'
import Head from 'expo-router/head'
import { useAppSession } from '@/src/contexts/AppSessionContext'
import { useTranslation } from 'react-i18next'

import type { VoucherRedeemErrorKey } from '../../../utils/group-code-errors'
import { Icon, symbolName } from '../../../components/ui/Icon'
import { KeyboardAwareScrollLayout } from '../../../components/ui/KeyboardAwareScrollLayout'
import { Spinner } from '../../../components/ui/Spinner'
import { useRedeemGroupCode } from '../../../queries/group-code-queries'
import { showAlert } from '../../../utils/alert'
import { mapRedeemErrorToKey } from '../../../utils/group-code-errors'

const ERROR_KEY_TO_I18N = {
  invalid_code: 'voucher:error_invalid_code',
  expired_code: 'voucher:error_expired_code',
  already_redeemed: 'voucher:error_already_redeemed',
  network_error: 'voucher:error_network',
  unknown_error: 'voucher:error_unknown',
} as const satisfies Record<VoucherRedeemErrorKey, string>

function normalizeVoucherCode(raw: string): string {
  return raw.trim().replace(/\s+/g, '')
}

export default function RedeemVoucher() {
  const { back, dismiss, replace } = useRouter()
  const insets = useSafeAreaInsets()
  const { refreshUserStatus } = useAppSession()
  const [code, setCode] = useState('')
  const [validationError, setValidationError] = useReducer(
    (_state: string | null, nextValue: string | null) => nextValue,
    null,
  )
  const { t } = useTranslation(['voucher', 'common'])
  const redeemMutation = useRedeemGroupCode()

  const redeemErrorMessage: string | null = redeemMutation.error
    ? t(ERROR_KEY_TO_I18N[mapRedeemErrorToKey(redeemMutation.error)])
    : null
  const clearErrors = () => {
    setValidationError(null)
    redeemMutation.reset()
  }

  const handleRedeem = () => {
    const normalized = normalizeVoucherCode(code)
    if (!normalized) {
      setValidationError(t('voucher:error_empty'))
      return
    }

    clearErrors()

    redeemMutation.mutate(normalized, {
      onSuccess: () => {
        setCode('')
        redeemMutation.reset()
        showAlert(t('voucher:success_title'), t('voucher:success_message'), [
          {
            text: t('common:ok'),
            onPress: async () => {
              await refreshUserStatus()
              dismiss()
              replace('/')
            },
          },
        ])
      },
    })
  }

  return (
    <>
      <Head>
        <title>{t('common:page_title_voucher')}</title>
      </Head>
      <KeyboardAwareScrollLayout
        className="flex-1 bg-background-primary"
        contentContainerClassName="flex-grow"
      >
        <View
          className="flex-row justify-between items-center px-5 pb-5"
          style={{ paddingTop: insets.top + 8 }}
        >
          <Pressable onPress={() => back()} className="p-1.5">
            <Icon name={symbolName('xmark', 'close')} size={28} color="ink" />
          </Pressable>
          <Text className="text-xl font-semibold">{t('voucher:title')}</Text>
          <View style={{ width: 28 }} />
        </View>

        <View className="p-6 items-center">
          <Text className="text-lg font-medium mb-5 text-ink">
            {t('voucher:label')}
          </Text>
          <TextInput
            className="w-full h-[50px] border border-border rounded-xl px-4 py-0 text-lg mb-2.5 bg-fill-input text-center tracking-widest"
            style={{
              lineHeight: 22,
              includeFontPadding: false,
            }}
            value={code}
            onChangeText={(text) => {
              setCode(text)
              clearErrors()
            }}
            placeholder={t('voucher:placeholder')}
            autoCapitalize="characters"
            autoCorrect={false}
            returnKeyType="done"
            onSubmitEditing={handleRedeem}
            editable={!redeemMutation.isPending}
            textAlignVertical="center"
          />

          {(validationError ?? redeemErrorMessage) != null && (
            <Text className="text-destructive mb-4 text-center">
              {validationError ?? redeemErrorMessage}
            </Text>
          )}

          <Pressable
            className={`w-full h-[50px] bg-primary rounded-xl justify-center items-center mt-2.5 mb-5 ${redeemMutation.isPending ? 'bg-gray-400' : ''}`}
            onPress={handleRedeem}
            disabled={redeemMutation.isPending}
          >
            {redeemMutation.isPending ? (
              <Spinner color="#FFF" />
            ) : (
              <Text className="text-primary-foreground text-base font-semibold">
                {t('voucher:button')}
              </Text>
            )}
          </Pressable>

          <Text className="text-center text-muted text-sm px-5">
            {t('voucher:info')}
          </Text>
        </View>
      </KeyboardAwareScrollLayout>
    </>
  )
}
